import type {
  AnnexAsset,
  BudgetOption,
  CloseGate,
  FieldMeta,
  FieldMetaMap,
  MigrationItem,
  MigrationItemAsset,
  MigrationItemDependency,
  MigrationItemEvent,
  MigrationItemStatus,
  MigrationPlan,
  MigrationPlanVersion,
  PartASectionKey,
  ProcurementOption,
  RescanResult,
  RiskFrameworkOption,
  SystemAnnex,
} from '@/api/types/migration'
import { ASSET_FIELDS, PROTOCOL_CATEGORY_LABEL, type ProtocolCategory, type ProtocolLine } from '@/api/types/migration'

// ── Pure helpers for the submission plan screens and the tracker ────────────
// The plan's rules (provenance, sync, checks, completeness) run in
// qtz-readiness; what stays here is what the screens compute for display
// (section catalogue, completeness counts over the rows the backend serves)
// and the Migration Tracker's close-out gate, which still runs in the browser.

export const DAY_MS = 86_400_000
export const fmtDay = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—')

// ── Part A section catalogue (CSA template, Part A — Organisational Level) ──

export interface OptionDef<O extends string = string> {
  value: O
  label: string
}

export interface PartASectionDef {
  key: PartASectionKey
  n: number
  title: string
  csa: string
  // The template's greyed "Example:" text, shown as the field's placeholder.
  example: string
  kind: 'text' | 'options' | 'table'
  options?: OptionDef[]
}

// Titles, prompts and tick-box wording are the Annex B template's, verbatim.
export const BUDGET_OPTIONS: OptionDef<BudgetOption>[] = [
  { value: 'allocated', label: 'Allocated for current financial year' },
  { value: 'under_review', label: 'Under review for current or upcoming financial year' },
  { value: 'plan_to_request', label: 'Plan to request budget for QS migration in upcoming financial year' },
  { value: 'not_committed', label: 'Not yet committed' },
  { value: 'other', label: 'Other (specify in elaboration)' },
]
// The "Not yet…" tick in each Part A tick-box section contradicts the status
// ticks (allocated, under review, planned…): ticking one clears the other.
// "Other" sits beside either.
export const NOT_YET_OPTIONS: ReadonlySet<string> = new Set(['not_committed', 'not_incorporated'])

export function toggleOption<O extends string>(selected: O[], o: O): O[] {
  if (selected.includes(o)) return selected.filter((x) => x !== o)
  if (o === 'other') return [...selected, o]
  const keep = NOT_YET_OPTIONS.has(o)
    ? selected.filter((x) => x === 'other')
    : selected.filter((x) => !NOT_YET_OPTIONS.has(x))
  return [...keep, o]
}

export const RISK_OPTIONS: OptionDef<RiskFrameworkOption>[] = [
  { value: 'documented', label: 'Quantum risk formally documented and included in the enterprise risk register' },
  { value: 'under_review', label: 'Quantum risk under review for inclusion in the risk management framework' },
  { value: 'plan_next_cycle', label: 'Plan to incorporate quantum risk in the upcoming review cycle' },
  { value: 'not_incorporated', label: 'Not yet incorporated' },
  { value: 'other', label: 'Other (specify in elaboration)' },
]
export const PROCUREMENT_OPTIONS: OptionDef<ProcurementOption>[] = [
  { value: 'incorporated', label: 'Quantum-safe requirements incorporated in procurement clauses and vendor contracts' },
  { value: 'under_review', label: 'Under review for inclusion in procurement policy or upcoming contract renewals' },
  { value: 'plan_next_review', label: 'Plan to incorporate quantum-safe requirements in the next procurement policy review' },
  { value: 'not_incorporated', label: 'Not yet incorporated' },
  { value: 'other', label: 'Other (specify in elaboration)' },
]

export const PART_A_SECTIONS: PartASectionDef[] = [
  { key: 's1_leadership', n: 1, title: 'Leadership Support', csa: 'Describe board / senior leadership engagement on quantum-safe migration. Identify the responsible board member or senior leader accountable for quantum-safe migration.', example: 'QS migration was tabled at the Board in Q4 2026 as part of the cyber risk briefing, with [Name/Role] designated as the accountable board member.', kind: 'text' },
  { key: 's2_budget', n: 2, title: 'Budget status', csa: 'Indicate the current status of budget for QS migration. Approximate range can be noted in the elaboration.', example: 'Budget under review for FY27; an indicative range of S$2–3m has been tabled, covering PoCs, and consultancy service.', kind: 'options', options: BUDGET_OPTIONS },
  { key: 's3_crypto_management', n: 3, title: 'Cryptography Management', csa: 'Describe the steps your organisation is taking to manage cryptographic assets systematically and achieve cryptoagility.', example: '• Build a centralised cryptography management platform or system and the target timeline, or describe current cryptographic management efforts\n• Tracking cryptographic dependencies and hardcoded implementations', kind: 'text' },
  { key: 's4_risk_framework', n: 4, title: 'Incorporation into existing risk management frameworks', csa: 'Indicate the current status of quantum-related risk incorporation into your organisation’s existing risk management framework(s).', example: 'Quantum risk has been added to the enterprise risk register as of Q3 2025, classified under technology and cyber risk. Currently rated high likelihood / high impact based on the organisation’s reliance on RSA and ECC across critical systems.', kind: 'options', options: RISK_OPTIONS },
  { key: 's5_procurement', n: 5, title: 'Procurement policy', csa: 'Indicate the current status of quantum-safe requirements in your organisation’s procurement policies and vendor management processes. Note: CIIOs must update procurement policies from 1 Jan 2028.', example: 'Procurement clauses have been updated as of Q4 2025 to require vendors to demonstrate a roadmap toward quantum-safe cryptographic standards for all new contracts involving cryptographic functionality. Three contracts have been identified where quantum-safe requirements cannot be enforced, but vendors hold proprietary solutions with no viable alternatives in the market, making replacement or parallel procurement unfeasible and interim compensating controls will be put in place.', kind: 'options', options: PROCUREMENT_OPTIONS },
  { key: 's6_challenges', n: 6, title: 'Potential Challenges and Support Needs', csa: 'Describe the challenges affecting your migration progress and the specific support needed to address them. Be specific: for each challenge, state the nature of the issue and the type of support needed — whether guidance, tools, direct technical assistance, or vendor engagement.', example: '', kind: 'table' },
]

// Part A §6 column headings and the template's example row.
export const CHALLENGE_COLUMNS = { blocker: 'Current blocker', support: 'Specific support that would help' } as const
export const CHALLENGE_EXAMPLE = {
  blocker: 'No established quantum-safe standards or vendor solutions exist for [specific sector/technology, e.g. operational technology/ industrial control systems]. This leaves the organisation without a viable migration pathway and exposed in the interim.',
  support: 'Interim guidance on risk-based mitigations and compensating controls to reduce exposure while sector-specific standards and solutions mature. This should include prioritisation criteria for identifying the highest-risk systems and practical measures that can be implemented within existing operational constraints.',
} as const

// A placeholder built from the template's example text.
export const exampleText = (example: string) => (example ? `Example: ${example}` : undefined)

// ── Part B section catalogue (CSA template, Part B — System Annex) ──────────
// Section titles and the prompt under each, verbatim from the template.
export const ANNEX_SECTIONS = {
  s1: { title: 'System Details', csa: '' },
  s2: { title: 'Details of Cryptographic Asset and Dependencies', csa: 'For each cryptographic asset — including cryptographic modules, systems, and applications such as PKI systems, HSMs, cryptographic libraries, and VPNs — provide the information in the table below, describing the service it provides and the protocols/algorithms in use.' },
  s3: { title: 'Operational Continuity and Potential Risks', csa: 'Describe how the organisation is planning to manage potential disruptions, if any, to operations during quantum-safe migration.' },
} as const

export const sectionDef = (key: PartASectionKey) => PART_A_SECTIONS.find((s) => s.key === key)!

// ── Field provenance (§1.2, Note 2) ─────────────────────────────────────────

export const metaOf = (map: FieldMetaMap | undefined, key: string): FieldMeta => map?.[key] ?? { source: 'manual', source_ref: null, synced_at: null, overridden: false }

// Fields the service pre-fills from Playbook (M3) and Discovery on sync. They
// stay editable: a sync keeps a person's answer, and Revert restores the
// pre-fill. The note says what the pre-fill is and what to add.
export const PLAYBOOK_PREFILL_HINT: Partial<Record<string, string>> = {
  will_migrate: "Pre-filled from Playbook (M3): the NIST replacement for each quantum-vulnerable finding and Discovery's remediation deadline. Edit it, e.g. No with a justification.",
  dependencies: 'Pre-filled from Discovery and the Playbook (M3) CA register: the systems that use this asset or share its certificates and keys. Edit it to add services or pipelines.',
  vendor_qs_roadmap: "Pre-filled from Playbook (M3). Add the vendor's PQC release date or advisory.",
}

// The assets a migration item covers (tracker).
export function assetIdsOf(itemId: string, itemAssets: MigrationItemAsset[]): string[] {
  return itemAssets.filter((x) => x.item_id === itemId).map((x) => x.asset_id)
}

// ── Completeness (§1.2, Note 3) ─────────────────────────────────────────────

const filled = (v: unknown) => typeof v === 'string' && v.trim().length > 0

export function partASectionAnswered(plan: MigrationPlan, key: PartASectionKey): boolean {
  switch (key) {
    case 's1_leadership': return filled(plan.s1_leadership)
    case 's3_crypto_management': return filled(plan.s3_crypto_management)
    case 's2_budget': return plan.s2_budget.options.length > 0 || filled(plan.s2_budget.elaboration)
    case 's4_risk_framework': return plan.s4_risk_framework.options.length > 0 || filled(plan.s4_risk_framework.elaboration)
    case 's5_procurement': return plan.s5_procurement.options.length > 0 || filled(plan.s5_procurement.elaboration)
    case 's6_challenges': return plan.s6_challenges.some((c) => filled(c.blocker))
  }
}

export function partACompleteness(plan: MigrationPlan) {
  const sections = PART_A_SECTIONS.map((s) => ({ ...s, answered: partASectionAnswered(plan, s.key) }))
  const answered = sections.filter((s) => s.answered).length
  const firstOpen = sections.find((s) => !s.answered) ?? null
  return { sections, answered, total: PART_A_SECTIONS.length, first_open: firstOpen }
}

// Nine counted fields per annex: system name, description, function; per
// asset (every block must answer) model/version, protocols & algorithms, will
// migrate, solution/timeline when Y, vendor roadmap; section 3 as one block.
export const ANNEX_FIELD_COUNT = 9

export function annexCompleteness(annex: SystemAnnex, blocks: AnnexAsset[]) {
  const open: string[] = []
  const every = (pred: (b: AnnexAsset) => boolean) => blocks.length > 0 && blocks.every(pred)
  const checks: Array<[string, boolean]> = [
    ['System name', filled(annex.system_name)],
    ['Brief description', filled(annex.description)],
    ['Function supported', filled(annex.function_supported)],
    ['Asset, model and version', every((b) => filled(b.asset_model_version))],
    ['Protocols and algorithms', every((b) => filled(b.protocols_algorithms))],
    ['Will this asset be migrated?', every((b) => b.will_migrate != null)],
    ['Solution and timeline', every((b) => b.will_migrate === 'N' || (b.will_migrate === 'Y' && filled(b.migrate_solution_timeline)))],
    ['Vendor quantum-safe roadmap', every((b) => filled(b.vendor_qs_roadmap))],
    ['Continuity and risks', filled(annex.s3_text)],
  ]
  checks.forEach(([label, ok]) => !ok && open.push(label))
  return { answered: ANNEX_FIELD_COUNT - open.length, total: ANNEX_FIELD_COUNT, open }
}

export function blockCompleteness(b: AnnexAsset) {
  const ok = ASSET_FIELDS.filter((f) => (f.key === 'will_migrate' ? b.will_migrate != null && (b.will_migrate === 'N' || filled(b.migrate_solution_timeline)) : filled(b[f.key] as string))).length
  return { answered: ok, total: ASSET_FIELDS.length }
}

export function annexFieldCounts(annex: SystemAnnex, blocks: AnnexAsset[]) {
  let auto = 0, manual = 0, open = 0
  const count = (row: { field_meta: FieldMetaMap }, key: string, value: unknown) => {
    const m = metaOf(row.field_meta, key)
    const has = typeof value === 'string' ? filled(value) : value != null
    if (!has) open++
    else if (m.source !== 'manual' && !m.overridden) auto++
    else manual++
  }
  ;(['system_name', 'description', 'function_supported', 's3_text'] as Array<keyof SystemAnnex & string>).forEach((k) => count(annex, k, annex[k]))
  blocks.forEach((b) => ASSET_FIELDS.forEach((f) => count(b, f.key, b[f.key])))
  return { auto, manual, open }
}

// Per-plan completeness: Part A (6 sections) and Part B (annex completeness
// over the CII systems; a CII system without an annex counts as 0) weighted equally.
export function planCompleteness(plan: MigrationPlan, annexes: SystemAnnex[], ciiCount: number): number {
  const a = partACompleteness(plan).answered / PART_A_SECTIONS.length
  const perAnnex = annexes.map((x) => annexCompleteness(x, x.blocks ?? []).answered / ANNEX_FIELD_COUNT)
  const denominator = Math.max(ciiCount, annexes.length)
  const b = denominator ? perAnnex.reduce((s, v) => s + v, 0) / denominator : 0
  return Math.round(((a + b) / 2) * 100)
}

// ── Close-out gate (§3, Note 4) ─────────────────────────────────────────────

export function latestRescan(itemId: string, rescans: RescanResult[]): RescanResult | null {
  return rescans.filter((r) => r.item_id === itemId).sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0] ?? null
}

export function canClose(item: MigrationItem, scopedAssetIds: string[], events: MigrationItemEvent[], rescans: RescanResult[]): CloseGate {
  if (!scopedAssetIds.length) return { ok: false, failing_asset_ids: [], scan_ref: null, reason: 'no_assets' }
  const scan = latestRescan(item.id, rescans)
  if (!scan) return { ok: false, failing_asset_ids: scopedAssetIds, scan_ref: null, reason: 'no_scan' }
  const lastChange = events
    .filter((e) => e.item_id === item.id && (e.event_type === 'scope_changed' || e.event_type === 'target_changed'))
    .map((e) => e.occurred_at)
    .sort()
    .pop()
  if (lastChange && scan.completed_at < lastChange) return { ok: false, failing_asset_ids: scopedAssetIds, scan_ref: scan, reason: 'stale_scan' }
  const failing = scopedAssetIds.filter((id) => {
    const o = scan.outcomes.find((x) => x.asset_id === id)
    return !o || o.vulnerable_present
  })
  return { ok: failing.length === 0, failing_asset_ids: failing, scan_ref: scan, reason: failing.length ? 'vulnerable' : null }
}

export const awaitingRescan = (item: MigrationItem, scoped: string[], events: MigrationItemEvent[], rescans: RescanResult[]) => item.status === 'verified' && !canClose(item, scoped, events, rescans).ok

// What the Close-out column shows (§8.1).
export function closeOutLabel(item: MigrationItem, scoped: string[], events: MigrationItemEvent[], rescans: RescanResult[]): { text: string; tone: 'warn' | 'ok' | 'info' | 'neutral' } {
  if (item.status === 'closed') {
    const s = latestRescan(item.id, rescans)
    return { text: s ? `Re-scan ${fmtDay(s.completed_at)} · clear` : 'Closed', tone: 'ok' }
  }
  if (item.status === 'verified' || item.status === 'in_progress') {
    const gate = canClose(item, scoped, events, rescans)
    if (gate.ok) return { text: `Re-scan ${fmtDay(gate.scan_ref!.completed_at)} · clear`, tone: 'ok' }
    if (gate.reason === 'vulnerable') return { text: `Re-scan: ${gate.failing_asset_ids.length} of ${scoped.length} still vulnerable`, tone: 'warn' }
    if (gate.reason === 'stale_scan') return { text: 'Re-scan older than last change', tone: 'warn' }
    if (item.status === 'verified') return { text: 'Re-scan not run', tone: 'warn' }
  }
  if (item.vendor_gated_year) return { text: `Vendor-gated · ${item.vendor_gated_year}`, tone: 'info' }
  return { text: '—', tone: 'neutral' }
}

export const isBlocked = (item: MigrationItem, items: MigrationItem[], deps: MigrationItemDependency[]) =>
  deps.some((d) => d.item_id === item.id && (items.find((x) => x.id === d.depends_on_item_id)?.status ?? 'closed') !== 'closed')

// Which Part A sections an objective feeds ("Feeds export", I1 §8.5).
export function partASectionsUsing(objectiveCode: string): string[] {
  const map: Record<string, string[]> = { 'GV-1': ['1. Leadership Support'], 'GV-2': ['1. Leadership Support'], 'RA-1': ['3. Cryptography Management', '4. Risk management frameworks'], 'RA-2': ['4. Risk management frameworks'], 'EE-1': ['5. Procurement policy'], 'TE-2': ['3. Cryptography Management'] }
  return map[objectiveCode] ?? []
}

// ── Version labels (§5, §11) ────────────────────────────────────────────────

// A saved copy's label is "v{n} · {suffix}": v{n} is the live draft and is
// fixed (saving or exporting never moves it); the suffix is the person's.
export const suggestedLabelSuffix = (date = new Date()) => `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()} update`
export const versionLabel = (n: number, suffix: string) => (suffix.trim() ? `v${n} · ${suffix.trim()}` : `v${n}`)

// The copies saved of one draft, oldest first.
export const copiesOf = (versions: MigrationPlanVersion[], n: number) => versions.filter((v) => (v.plan_version ?? 1) === n)

// CSA asks a revised plan (v2 and later) to state its key changes.
export const noteRequired = (n: number) => n >= 2

export function statusSummary(items: MigrationItem[]): Record<MigrationItemStatus, number> {
  const out: Record<MigrationItemStatus, number> = { not_started: 0, planned: 0, in_progress: 0, verified: 0, closed: 0 }
  items.forEach((i) => (out[i.status] += 1))
  return out
}

// ── Protocols and algorithms (Part B section 2) ─────────────────────────────

export const lineText = (l: ProtocolLine) => [l.name, l.key_size ? `${l.key_size}-bit` : '', l.protocol_version, l.primitive].filter(Boolean).join(' · ')

const CATEGORY_ORDER: ProtocolCategory[] = ['algorithm', 'protocol', 'certificate', 'other']

// The asset's lines grouped by category, in reading order, with the findings
// each group holds. Empty when the asset has none (manual row, old version).
export function protocolGroups(b: AnnexAsset): Array<{ category: ProtocolCategory; label: string; lines: ProtocolLine[]; findings: number }> {
  const lines = b.protocol_lines ?? []
  if (!lines.length) return []
  return CATEGORY_ORDER.map((category) => {
    const ls = lines.filter((l) => l.category === category).sort((x, y) => y.count - x.count || lineText(x).localeCompare(lineText(y)))
    return { category, label: PROTOCOL_CATEGORY_LABEL[category], lines: ls, findings: ls.reduce((n, l) => n + l.count, 0) }
  }).filter((g) => g.lines.length > 0)
}

// The grouped lines as the export prints them: one paragraph per category.
// A field a person rewrote prints as they wrote it.
export function protocolsGroupedText(b: AnnexAsset): string {
  const groups = metaOf(b.field_meta, 'protocols_algorithms').overridden ? [] : protocolGroups(b)
  if (!groups.length) return b.protocols_algorithms
  return groups.map((g) => `${g.label}: ${g.lines.map((l) => `${lineText(l)}${l.count > 1 ? ` (×${l.count})` : ''}`).join('; ')}`).join('\n')
}

// ── Field hints ─────────────────────────────────────────────────────────────

// CSA asks for the accountable executive to be C-suite or equivalent. A title
// that names none of the usual forms gets a warning; it never blocks, since
// equivalents vary.
const C_SUITE_RE = /\b(chief|c[a-z]{1,3}o|managing director|president|executive director|director[- ]general|chair(man|woman|person)?|board member|group head)\b/i
export const looksCSuite = (title: string) => C_SUITE_RE.test(title)

// An asset name with no version number in it, e.g. "jPOS" from Discovery.
export const lacksVersion = (assetModelVersion: string) => !!assetModelVersion.trim() && !/\d/.test(assetModelVersion)
